// src/utils/dateUtils.js

/**
 * Shifts a date to today/tomorrow while preserving the original time
 * @param {string} originalISOString - ISO datetime string
 * @returns {string} - New ISO datetime string with shifted date
 */
export const shiftDatePreserveTime = (originalISOString) => {
    const now = new Date();
    
    if (!originalISOString) {
      return new Date(now.getTime() + 30 * 60000).toISOString();
    }
    
    const originalDate = new Date(originalISOString);
    const originalTime = {
      hours: originalDate.getHours(),
      minutes: originalDate.getMinutes(),
      seconds: originalDate.getSeconds(),
      milliseconds: originalDate.getMilliseconds(),
    };
    
    // Create new date with today's date but original time
    const newDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      originalTime.hours,
      originalTime.minutes,
      originalTime.seconds,
      originalTime.milliseconds
    );
    
    // If the new time is in the past, add one day
    if (newDate.getTime() < now.getTime()) {
      newDate.setDate(newDate.getDate() + 1);
    }
    
    return newDate.toISOString();
  };
  
  /**
   * Calculates the number of days between two ISO datetime strings
   * @param {string} pickupISO - Pickup datetime ISO string
   * @param {string} targetISO - Target datetime ISO string
   * @returns {number} - Number of days difference
   */
  export const calculateDayOffset = (pickupISO, targetISO) => {
    if (!pickupISO || !targetISO) return 1; // default 1 day
    
    const pickupDate = new Date(pickupISO);
    const targetDate = new Date(targetISO);
    
    const pickupDay = new Date(pickupDate.getFullYear(), pickupDate.getMonth(), pickupDate.getDate());
    const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    
    const dayDiff = Math.round((targetDay.getTime() - pickupDay.getTime()) / (1000 * 60 * 60 * 24));
    return dayDiff || 1;
  };
  
  /**
   * Formats an ISO datetime string to a localized time string (HH:MM)
   * @param {string} isoString - ISO datetime string
   * @returns {string} - Formatted time string
   */
  export const formatTime = (isoString) => {
    if (!isoString) return '';
    
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };
  
  /**
   * Formats an ISO datetime string to a short date string (e.g., "Jan 5")
   * @param {string} isoString - ISO datetime string
   * @returns {string} - Formatted date string
   */
  export const formatShortDate = (isoString) => {
    if (!isoString) return '';
    
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };
  
  /**
   * Checks if a date is today
   * @param {Date} date - Date object to check
   * @returns {boolean}
   */
  export const isToday = (date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const checkDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return checkDay.getTime() === today.getTime();
  };
  
  /**
   * Checks if a date is tomorrow
   * @param {Date} date - Date object to check
   * @returns {boolean}
   */
  export const isTomorrow = (date) => {
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    tomorrow.setDate(tomorrow.getDate() + 1);
    const checkDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return checkDay.getTime() === tomorrow.getTime();
  };
  
  /**
   * Checks if a datetime is within the next hour
   * @param {string} isoString - ISO datetime string
   * @returns {boolean}
   */
  export const isWithinNextHour = (isoString) => {
    if (!isoString) return false;
    
    const now = new Date();
    const targetTime = new Date(isoString);
    const timeDiff = targetTime.getTime() - now.getTime();
    const oneHour = 60 * 60 * 1000;
    
    return timeDiff > 0 && timeDiff <= oneHour;
  };
  
  /**
   * Formats a pickup time label with relative date (Today, Tomorrow, or date)
   * @param {string} pickupTimeISO - Pickup time ISO string
   * @param {string} latestPickupTimeISO - Latest pickup time ISO string (optional)
   * @param {boolean} hasTimeWindow - Whether time window is set
   * @param {function} t - Translation function
   * @returns {string} - Formatted label
   */
  export const formatPickupTimeLabel = (pickupTimeISO, latestPickupTimeISO, hasTimeWindow, t) => {
    if (!pickupTimeISO) {
      return t("delivery.pickupNow") || "Pickup Now";
    }
  
    const pickupDateTime = new Date(pickupTimeISO);
    
    // Check if date is valid
    if (isNaN(pickupDateTime.getTime())) {
      return t("delivery.pickupNow") || "Pickup Now";
    }
  
    // Check if within next hour and no time window
    if (isWithinNextHour(pickupTimeISO) && !hasTimeWindow) {
      return t("delivery.pickupNow") || "Pickup Now";
    }
  
    const timeStr = formatTime(pickupTimeISO);
    
    let label = "";
    if (isToday(pickupDateTime)) {
      label = ` ${t("delivery.today")} ${timeStr}`;
    } else if (isTomorrow(pickupDateTime)) {
      label = ` ${t("delivery.tomorrow")} ${timeStr}`;
    } else {
      const dateStr = formatShortDate(pickupTimeISO);
      label = `${dateStr} ${timeStr}`;
    }
  
    // Add time window indicator if set
    if (hasTimeWindow && latestPickupTimeISO) {
      const latestTime = formatTime(latestPickupTimeISO);
      label += ` - ${latestTime}`;
    }
  
    return label;
  };
  
  /**
   * Formats a datetime for display with relative date
   * @param {string} isoString - ISO datetime string
   * @param {function} t - Translation function
   * @returns {string} - Formatted datetime string
   */
  export const formatDateTime = (isoString, t) => {
    if (!isoString) return t('delivery.notSet') || 'Not set';
    
    const date = new Date(isoString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return t('delivery.notSet') || 'Not set';
    }
    
    const timeStr = formatTime(isoString);
    
    if (isToday(date)) {
      return `${t('delivery.today')} ${timeStr}`;
    } else if (isTomorrow(date)) {
      return `${t('delivery.tomorrow')} ${timeStr}`;
    } else {
      const dateStr = formatShortDate(isoString);
      return `${dateStr} ${timeStr}`;
    }
  };
  
  /**
   * Shifts delivery times for resending (preserves time offsets, updates date to today)
   * @param {object} delivery - Original delivery object
   * @returns {object} - Object with shifted times
   */
  export const shiftDeliveryTimes = (delivery) => {
    const now = new Date();
    
    // Validate required fields
    if (!delivery.pickupTime || !delivery.targetTime) {
      console.error('Missing required time fields in delivery:', delivery);
      return {
        pickupTime: new Date(now.getTime() + 30 * 60000).toISOString(),
        targetTime: new Date(now.getTime() + 3 * 60 * 60000).toISOString(),
        latestPickupTime: new Date(now.getTime() + 90 * 60000).toISOString(),
        earliestTargetTime: new Date(now.getTime() + 150 * 60000).toISOString(),
      };
    }
    
    // Shift pickup time to today, preserving the original time of day
    const newPickupTime = shiftDatePreserveTime(delivery.pickupTime);
    const newPickupDate = new Date(newPickupTime);
    
    // Validate new pickup date
    if (isNaN(newPickupDate.getTime())) {
      console.error('Invalid newPickupDate:', newPickupTime);
      return {
        pickupTime: new Date(now.getTime() + 30 * 60000).toISOString(),
        targetTime: new Date(now.getTime() + 3 * 60 * 60000).toISOString(),
        latestPickupTime: new Date(now.getTime() + 90 * 60000).toISOString(),
        earliestTargetTime: new Date(now.getTime() + 150 * 60000).toISOString(),
      };
    }
    
    // Calculate the ORIGINAL time differences in milliseconds
    const originalPickup = new Date(delivery.pickupTime);
    const originalTarget = new Date(delivery.targetTime);
    
    // Validate original dates
    if (isNaN(originalPickup.getTime()) || isNaN(originalTarget.getTime())) {
      console.error('Invalid original dates:', {
        pickupTime: delivery.pickupTime,
        targetTime: delivery.targetTime
      });
      return {
        pickupTime: newPickupTime,
        targetTime: new Date(newPickupDate.getTime() + 3 * 60 * 60000).toISOString(),
        latestPickupTime: new Date(newPickupDate.getTime() + 60 * 60000).toISOString(),
        earliestTargetTime: new Date(newPickupDate.getTime() + 150 * 60000).toISOString(),
      };
    }
    
    const pickupToTargetMs = originalTarget.getTime() - originalPickup.getTime();
    
    // Apply the original offset to the new pickup time
    const newTargetDate = new Date(newPickupDate.getTime() + pickupToTargetMs);
    
    // Validate new target date
    if (isNaN(newTargetDate.getTime())) {
      console.error('Invalid newTargetDate. pickupToTargetMs:', pickupToTargetMs);
      return {
        pickupTime: newPickupTime,
        targetTime: new Date(newPickupDate.getTime() + 3 * 60 * 60000).toISOString(),
        latestPickupTime: new Date(newPickupDate.getTime() + 60 * 60000).toISOString(),
        earliestTargetTime: new Date(newPickupDate.getTime() + 150 * 60000).toISOString(),
      };
    }
    
    const newTargetTime = newTargetDate.toISOString();
    
    // Shift latest pickup time if exists (preserve original offset from pickup)
    let newLatestPickupTime = null;
    if (delivery.hasTimeWindow && delivery.latestPickupTime) {
      const originalLatest = new Date(delivery.latestPickupTime);
      if (!isNaN(originalLatest.getTime())) {
        const pickupToLatestMs = originalLatest.getTime() - originalPickup.getTime();
        const newLatest = new Date(newPickupDate.getTime() + pickupToLatestMs);
        if (!isNaN(newLatest.getTime())) {
          newLatestPickupTime = newLatest.toISOString();
        }
      }
    }
    
    // Shift earliest target time if exists (preserve original offset from target)
    let newEarliestTargetTime = null;
    if (delivery.hasTimeWindow && delivery.earliestTargetTime) {
      const originalEarliest = new Date(delivery.earliestTargetTime);
      if (!isNaN(originalEarliest.getTime())) {
        const targetToEarliestMs = originalEarliest.getTime() - originalTarget.getTime();
        const newEarliest = new Date(newTargetDate.getTime() + targetToEarliestMs);
        if (!isNaN(newEarliest.getTime())) {
          newEarliestTargetTime = newEarliest.toISOString();
        }
      }
    }
    
    return {
      pickupTime: newPickupTime,
      targetTime: newTargetTime,
      latestPickupTime: newLatestPickupTime || new Date(newPickupDate.getTime() + 60 * 60000).toISOString(),
      earliestTargetTime: newEarliestTargetTime || new Date(newTargetDate.getTime() - 30 * 60000).toISOString(),
    };
  };